import * as THREE from "three";
import Experience from "../Experience";
import {
	linesToBillboardEls,
	billboardEls,
	interactedObjects,
	upcomingConnections,
	landingStuff,
} from "../World/sets";
import SpriteText from "three-spritetext";
import { white, brown, walla, lineLight, lineDark, defaultLineW } from "./styles";
import { LineMaterial } from "three-fatline";
import { debugLog } from "./debugLog";
import { gsap } from "gsap";

type SceneNode = THREE.Object3D & {
	userData: SceneNodeUserData;
	children: SceneNode[];
	visible: boolean;
	name: string;
	type: string;
};
type SceneNodeUserData = {
	regen?: boolean;
	isBetweenInteracted?: boolean;
	contentRendered?: boolean;
	active?: boolean;
	blockType?: string;
	creationDate?: string;
	title?: string;
	user?: { username?: string };
	connections?: unknown[];
	connection?: unknown[];
};
type MeshLike = SceneNode & {
	type: "Mesh";
	geometry?: {
		parameters?: { width?: number; height?: number };
		dispose?: () => void;
	};
	material?:
		| ({
				dispose?: () => void;
				map?: { dispose?: () => void };
		  } | null)
		| Array<{
				dispose?: () => void;
				map?: { dispose?: () => void };
		  } | null>;
};
type SceneObjectWithChildren = SceneNode & { children: SceneNode[] };
type SpriteLabel = SpriteText & { text?: string };
type LineNode = SceneNode & { material?: THREE.Material | LineMaterial };
export default class SceneUtils {
	experience: Experience;
	scene: THREE.Scene;
	sizes: { width: number; height: number; pixelRatio?: number };
	private readonly billboardFrustum = new THREE.Frustum();
	private readonly billboardProjectionMatrix = new THREE.Matrix4();
	private billboardFrustumReady = false;
	private invalidBillboardLogKeys = new Set<string>();
	constructor(scene: THREE.Scene) {
		this.experience = new Experience();
		this.scene = scene;
		this.sizes = this.experience.sizes;
	}
	beginBillboardFrame() {
		const camera = this.experience.camera?.instance;
		if (!camera) {
			this.billboardFrustumReady = false;
			return;
		}
		camera.updateMatrixWorld();
		this.billboardProjectionMatrix.multiplyMatrices(
			camera.projectionMatrix,
			camera.matrixWorldInverse,
		);
		this.billboardFrustum.setFromProjectionMatrix(this.billboardProjectionMatrix);
		this.billboardFrustumReady = true;
	}
	/** @param {string} value */
	isValidNumber = (value: string) => {
		return /^\d+$/.test(value); // Uses regular expression to check if value consists only of digits
	};
	findInteractedLines(el: SceneNode) {
		return (
			(el instanceof THREE.Line && el.userData.isBetweenInteracted) ||
			(el.type === "Line2" && el.userData.isBetweenInteracted) ||
			(el.type === "Line2" && el.userData.regen) ||
			(el.children.length > 0 &&
				el.children.forEach((child: SceneNode) => {
					debugLog(child);
					child.type === "Line2" && el.userData.isBetweenInteracted;
				}))
		);
	}
	findLinesToBillboardEls(el: SceneNode) {
		return linesToBillboardEls.has(el);
	}
	findElwithChildren(el: SceneNode) {
		return el.type === "Object3D" && el.children.length > 0;
	}
	findRegenChannels(el: SceneNode) {
		return el.userData && el.userData.regen;
	}
	findPathLineGroup(el: SceneNode) {
		return el.type === "Group" && el.name === "pathLineGroup";
	}
	findCamLineGroup(el: SceneNode) {
		return el.type === "Group" && el.name === "camLineGroup";
	}
	findRenderedChannels(el: SceneNode) {
		return el.userData && el.userData.contentRendered;
	}
	findActiveChannels(el: SceneNode) {
		return el.userData && el.userData.active;
	}
	findTextBlocks(el: SceneNode) {
		return el.userData && el.userData.blockType === "Text";
	}
	findChannelBlocks(el: SceneNode) {
		return el.userData && el.userData.blockType === "Channel";
	}
	findBillboardEls(el: SceneNode) {
		return billboardEls.has(el);
	}
	setVisibleWhen(el: SceneNode, predicate: (node: SceneNode) => unknown) {
		if (predicate(el)) {
			el.visible = true;
		}
	}
	showUpcomingConnections(el: SceneNode) {
		if (this.findElwithChildren(el)) {
			el.children.forEach((subchild: SceneNode) => {
				if (subchild.userData && upcomingConnections.has(subchild)) {
					if (this.findChannelBlocks(subchild) && !subchild.userData.contentRendered)
						el.visible = true;
					subchild.visible = true;
				}
			});
		}
	}
	showInteractedObjects() {
		interactedObjects.forEach((object: SceneNode) => {
			object.visible = true;
		});
	}
	showLandingEls() {
		landingStuff.forEach((object) => {
			if (!object) return;
			object.visible = true;
		});
	}
	showPathLineGroup() {
		const pathLineGroup = this.scene.getObjectByName("pathLineGroup");
		if (pathLineGroup && pathLineGroup.children.length > 0) {
			pathLineGroup.children.forEach((subchild: THREE.Object3D) => {
				if (subchild.name === "PathLine") {
					pathLineGroup.visible = true;
					subchild.visible = true;
				}
			});
		}
	}
	showCameraLine(el: SceneNode) {
		const cameraLine = this.scene.getObjectByName("CameraLine");
		if (this.experience.showCamLines) {
			if (cameraLine) {
				cameraLine.visible = true;
			}
			if (this.findCamLineGroup(el)) {
				el.visible = true;
			}
		}
	}
	hideCameraLine(el: SceneNode) {
		const cameraLine = this.scene.getObjectByName("CameraLine");
		if (this.experience.showCamLines) {
			if (cameraLine) {
				cameraLine.visible = false;
			}
			if (this.findCamLineGroup(el)) {
				el.visible = false;
			}
		}
	}
	showBillboardEls(el: SceneNode) {
		this.setVisibleWhen(el, this.findBillboardEls.bind(this));
	}
	showRenderedChannels(el: SceneNode) {
		this.setVisibleWhen(el, this.findRenderedChannels.bind(this));
	}
	showActiveChannels(el: SceneNode) {
		this.setVisibleWhen(el, this.findActiveChannels.bind(this));
	}
	showRegenChannels(el: SceneNode) {
		this.setVisibleWhen(el, this.findRegenChannels.bind(this));
	}
	showLineToBillboardEls(el: SceneNode) {
		this.setVisibleWhen(el, this.findLinesToBillboardEls.bind(this));
	}
	showNestedActiveSprites(el: SceneNode) {
		if (this.findElwithChildren(el)) {
			el.children.forEach((child: SceneNode) => {
				if (this.findChannelBlocks(child) && this.findRenderedChannels(child)) {
					el.visible = true;
					child.visible = true;
				}
				if (this.findTextBlocks(child)) {
					// might be problematic
					el.visible = true;
					child.visible = true;
				}
			});
		}
	}
	showInteractedLines(el: SceneNode) {
		this.setVisibleWhen(el, this.findInteractedLines.bind(this));
	}
	showBlockInfo(object: SceneObjectWithChildren) {
		if (object.type === "Mesh" && object.children.length > 0) {
			object.children.forEach((child: SceneNode) => {
				child.visible = true;
			});
		}
		if (object.type === "Sprite" && object.children.length > 0) {
			object.children.forEach((child: SceneNode) => {
				child.visible = true;
			});
		}
	}
	hideBlockInfo(object: SceneObjectWithChildren) {
		if (object.type === "Mesh" && object.children && object.children[0]) {
			object.children.forEach((child: SceneNode) => {
				child.visible = false;
			});
		}
		if (object.type === "Sprite" && object.children && object.children[0]) {
			object.children.forEach((child: SceneNode) => {
				child.visible = false;
			});
		}
	}
	/** @param {string} text @param {number} maxLength */
	cropText(text: string, maxLength: number) {
		if (text.length > maxLength) {
			return text.slice(0, maxLength) + "...";
		} else {
			return text;
		}
	}
	calculateLabelWidth(label: SceneNode) {
		const bbox = new THREE.Box3().setFromObject(label);
		return bbox.max.x - bbox.min.x;
	}
	positionLabels(object: SceneNode, labels: SceneNode[]) {
		let offsetX: number;
		if (object.type === "Mesh" && (object as MeshLike).geometry?.parameters?.width) {
			offsetX = -((object as MeshLike).geometry?.parameters?.width ?? 1) / 2;
		} else {
			offsetX = -0.5;
		}
		// console.log(object, labels, this.experience.camera);
		labels.forEach((label: SceneNode) => {
			label.position.x = offsetX;
			offsetX += this.calculateLabelWidth(label) + 0.02; // Add some spacing between labels
		});
	}
	async removeExistingLabels(object: MeshLike) {
		const labelsToRemove = [
			"blockTypeLabel",
			"connectionCountLabel",
			"dateLabel",
			"userLabel",
		];
		object.children.forEach((child: SceneNode) => {
			if (labelsToRemove.includes(child.name)) {
				object.remove(child);
				const childWithDisposables = child as SceneNode & {
					geometry?: { dispose?: () => void };
					material?:
						| ({ dispose?: () => void; map?: { dispose?: () => void } } | null)
						| Array<{ dispose?: () => void; map?: { dispose?: () => void } } | null>;
				};
				childWithDisposables.geometry?.dispose?.();
				const childMaterial = childWithDisposables.material;
				if (Array.isArray(childMaterial)) {
					childMaterial.forEach((material) => {
						material?.map?.dispose?.();
						material?.dispose?.();
					});
				} else {
					childMaterial?.map?.dispose?.();
					childMaterial?.dispose?.();
				}
			}
		});
		debugLog(object);
	}
	async updateConnectionCountLabel(object: SceneObjectWithChildren) {
		let connectionsLength;
		if (object?.userData?.connections && object?.userData?.connections !== undefined) {
			connectionsLength = object.userData.connections.length;
		} else if (
			object?.userData?.connection &&
			object?.userData?.connection !== undefined
		) {
			connectionsLength = object.userData.connection.length;
		} else {
			return;
		}
		let textInput = `${connectionsLength} crossroads`;
		let connectionCountLabel = object.children.find(
			(child: SceneNode) => child.name === "connectionCountLabel",
		) as SpriteLabel | undefined;

		if (connectionCountLabel) {
			connectionCountLabel.text = textInput;
			connectionCountLabel.visible = true;
		} else {
			connectionCountLabel = this.createConnectionCountLabel(object);
			connectionCountLabel.text = textInput;
			connectionCountLabel.visible = true;
		}
	}
	async createDataLabels(object: SceneObjectWithChildren) {
		const labels: Array<SceneNode | undefined> = [];
		labels.push(this.createBlockTypeLabel(object));
		labels.push(this.createDateLabel(object));
		labels.push(this.createUserLabel(object));
		const validLabels = labels.filter((label): label is SceneNode => label !== undefined);
		this.positionLabels(object, validLabels);
		this.billboardPlane(object);
	}
	billboardPlane(element: THREE.Object3D) {
		const camera = this.experience.camera?.instance;
		if (!element.visible) {
			return;
		}
		if (this.billboardFrustumReady) {
			try {
				// Some imported/disposed objects can have missing geometry bounds.
				if (!this.billboardFrustum.intersectsObject(element)) {
					return;
				}
			} catch (error) {
				const key = `${element.uuid}:${element.name}`;
				if (!this.invalidBillboardLogKeys.has(key)) {
					this.invalidBillboardLogKeys.add(key);
					debugLog("[pathways] skip billboard invalid bounds", element?.name, error);
				}
				billboardEls.delete(element);
				const interactedIndex = interactedObjects.indexOf(element);
				if (interactedIndex !== -1) {
					interactedObjects.splice(interactedIndex, 1);
				}
				return;
			}
		}
		if (element && element.position && camera) {
			const rotationQuaternion = camera.quaternion.clone();
			gsap.to(element.quaternion, {
				duration: 1,
				x: rotationQuaternion.x,
				y: rotationQuaternion.y,
				z: rotationQuaternion.z,
				w: rotationQuaternion.w,
				ease: "power2.out",
			});
			return;
		}
		console.error("Element or camera is undefined. Element:", element, "Camera:", camera);
	}
	createBaseDataLabel(textInput: string, name: string) {
		const label = new SpriteText(textInput, 1, white);
		label.backgroundColor = brown;
		label.padding = 3;
		label.fontFace = walla;
		label.center = new THREE.Vector2(0, 0.5);
		label.visible = false;
		label.name = name;
		label.scale.set(label.scale.x / 120, label.scale.y / 120, 0);
		return label;
	}
	applyDataLabelPosition(label: SpriteText, object: SceneObjectWithChildren) {
		if (object.type === "Sprite") {
			label.center = new THREE.Vector2(0, 1);
			label.position.set(-0.5, -0.54, 0);
			label.scale.y *= object.scale.x / object.scale.y;
			return;
		}
		if (object.type === "Mesh") {
			const mesh = object as MeshLike;
			const width = mesh.geometry?.parameters?.width ?? 0;
			const height = mesh.geometry?.parameters?.height ?? 0;
			label.position.set(-width * 2, (-height - 0.12) / 2, 0);
		}
	}
	createBlockTypeLabel(object: SceneObjectWithChildren) {
		let textInput;
		if (object.type === "Sprite") {
			textInput = "Text";
		} else if (object.type === "Mesh") {
			textInput = object.userData.blockType;
		}
		const blockTypeLabel = this.createBaseDataLabel(
			String(textInput ?? ""),
			"blockTypeLabel",
		);
		this.applyDataLabelPosition(blockTypeLabel, object);
		object.add(blockTypeLabel);
		return blockTypeLabel;
	}
	createConnectionCountLabel(object: SceneObjectWithChildren, hideInitially = false) {
		let connectionsLength;
		if (object?.userData?.connections && object?.userData?.connections !== undefined) {
			connectionsLength = object.userData.connections.length;
		} else if (
			object?.userData?.connection &&
			object?.userData?.connection !== undefined
		) {
			connectionsLength = object.userData.connection.length;
		}
		const textInput = `${connectionsLength} crossroads`;
		const connectionCountLabel = this.createBaseDataLabel(
			textInput,
			"connectionCountLabel",
		);
		this.applyDataLabelPosition(connectionCountLabel, object);
		if (hideInitially) {
			connectionCountLabel.visible = false; // Hide initially
		}
		object.add(connectionCountLabel);
		return connectionCountLabel;
	}
	createDateLabel(object: SceneObjectWithChildren) {
		if (object?.userData?.creationDate) {
			const textInput = `${object.userData.creationDate}`;
			const dateLabel = this.createBaseDataLabel(textInput, "dateLabel");
			this.applyDataLabelPosition(dateLabel, object);
			object.add(dateLabel);
			return dateLabel;
		}
	}
	createUserLabel(object: SceneObjectWithChildren) {
		if (object?.userData?.user) {
			const textInput = `\u263B ${object.userData.user.username ?? ""}`;
			const userLabel = this.createBaseDataLabel(textInput, "userLabel");
			this.applyDataLabelPosition(userLabel, object);
			object.add(userLabel);
			return userLabel;
		}
	}
	createDataLabel(object: SceneObjectWithChildren) {
		const data = object.userData;
		let textInput = "";
		data?.title ? (textInput += `${this.cropText(data.title, 20)}`) : (textInput += "");
		data?.creationDate ? (textInput += `${data.creationDate}`) : (textInput += "");
		data?.user ? (textInput += `by ${data.user.username}`) : (textInput += "");
		data?.connections ? (textInput += `${data.connections.length}`) : (textInput += "");
	}
	findAllCrossroadsLines() {
		this.scene.children.forEach((child: SceneNode) => {
			if (
				child.type === "Line2" &&
				child.name !== "CameraLine" &&
				child.name !== "PathLine"
			) {
				const lineChild = child as LineNode;
				const nextColor = document.body.classList.contains("day") ||
					document.body.classList.contains("dawn")
					? lineLight
					: lineDark;
				const existingMaterial = lineChild.material;
				if (existingMaterial instanceof LineMaterial) {
					existingMaterial.color.set(new THREE.Color(nextColor).getHex());
					existingMaterial.linewidth = Number(defaultLineW);
					existingMaterial.opacity = 0.5;
					existingMaterial.transparent = true;
					existingMaterial.resolution.set(this.sizes.width, this.sizes.height);
					existingMaterial.needsUpdate = true;
					return;
				}
				if (
					document.body.classList.contains("day") ||
					document.body.classList.contains("dawn")
				) {
					lineChild.material = new LineMaterial({
						color: new THREE.Color(lineLight).getHex(),
						linewidth: Number(defaultLineW),
						opacity: 0.5,
						transparent: true,
						resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
					});
				} else {
					lineChild.material = new LineMaterial({
						color: new THREE.Color(lineDark).getHex(),
						linewidth: Number(defaultLineW),
						opacity: 0.5,
						transparent: true,
						resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
					});
				}
			}
		});
	}
	hideAllCameraLines() {
		this.experience.showCamLines = false;
		this.scene.children.forEach((child: SceneNode) => {
			if (child.type === "Line2" && child.name === "CameraLine") {
				child.visible = false;
			}
			if (
				child.type === "Group" &&
				child.name === "camLineGroup" &&
				child.children.length > 0
			) {
				child.children.forEach((subchild: SceneNode) => {
					if (subchild.type === "Line2" && subchild.name === "CameraLine") {
						child.visible = false;
						subchild.visible = false;
					}
				});
			}
		});
	}
	showAllCameraLines() {
		this.experience.showCamLines = true;
		this.scene.children.forEach((child: SceneNode) => {
			if (child.type === "Line2" && child.name === "CameraLine") {
				child.visible = true;
			}
			if (
				child.type === "Group" &&
				child.name === "camLineGroup" &&
				child.children.length > 0
			) {
				child.children.forEach((subchild: SceneNode) => {
					if (subchild.type === "Line2" && subchild.name === "CameraLine") {
						child.visible = true;
						subchild.visible = true;
					}
				});
			}
		});
	}
}
